import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button, message } from 'antd';
import UploadDocumentModal from './components/UploadDocumentModal';
import DocumentsTable from './components/DocumentsTable';
import SendFaxDrawer from 'pages/dashboard/EFaxPage/EFaxDashboardPage/SendFaxDrawer';
import type { SendFaxInitialAttachment } from 'pages/dashboard/EFaxPage/EFaxDashboardPage/SendFaxDrawer';
import useFaxNumber from 'hooks/useFaxNumber';
import { faxContactList } from 'api/fax';
import type { FaxContact } from 'types/eFax';
import type { IGetDocumentItem } from 'types/document';
import {
    extractFaxContactListPage,
    isFaxScheduleSuccess,
    mapFaxContactVoToFaxContact,
} from 'utils/eFaxScheduleRows';
import { isEfaxSubscriptionCanceledStatus } from 'utils/eFaxSubscriptionStatus';
import s from './s.module.less';

interface Props {
    clientId?: number;
    isGroupPractice?: boolean;
}

const Documents = ({ clientId, isGroupPractice }: Props) => {
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [hasDocuments, setHasDocuments] = useState(false);
    const documentsTableRef = useRef<{ refreshData: () => void }>(null);

    const [faxNumber] = useFaxNumber();
    const [faxContacts, setFaxContacts] = useState<FaxContact[]>([]);
    const [sendFaxOpen, setSendFaxOpen] = useState(false);
    const [sendFaxPrefill, setSendFaxPrefill] = useState<SendFaxInitialAttachment | null>(null);

    const faxInactive =
        !faxNumber?.faxNumber || isEfaxSubscriptionCanceledStatus(faxNumber.status);

    const fetchFaxContacts = useCallback(async () => {
        const result = await faxContactList({ current: 1, size: 200 });
        const body = result?.data;
        if (!result?.error && body && isFaxScheduleSuccess(body)) {
            const { records } = extractFaxContactListPage(body);
            setFaxContacts(records.map(mapFaxContactVoToFaxContact));
        }
    }, []);

    useEffect(() => {
        if (!sendFaxOpen) return;
        void fetchFaxContacts();
    }, [sendFaxOpen, fetchFaxContacts]);

    const handleUpload = () => {
        setShowUploadModal(true);
    };

    const handleSendFaxFromDocument = useCallback(
        (doc: IGetDocumentItem) => {
            if (!clientId) return;
            if (faxInactive) {
                message.warning('Activate eFax from the sidebar before sending faxes.');
                return;
            }
            setSendFaxPrefill({ clientId, documents: [doc] });
            setSendFaxOpen(true);
        },
        [clientId, faxInactive],
    );

    const closeSendFax = () => {
        setSendFaxOpen(false);
        setSendFaxPrefill(null);
    };

    return (
        <div className={s.wrap}>
            {hasDocuments && (
                <div className={s.header}>
                    <Button type="primary" onClick={() => setShowUploadModal(true)}>
                        Upload Document
                    </Button>
                </div>
            )}
            <DocumentsTable
                clientId={clientId}
                onUpload={handleUpload}
                onHasDocumentsChange={setHasDocuments}
                ref={documentsTableRef}
                onSendFax={handleSendFaxFromDocument}
                isGroupPractice={isGroupPractice}
            />
            <UploadDocumentModal
                open={showUploadModal}
                clientId={clientId ?? 0}
                onClose={() => setShowUploadModal(false)}
                onSuccess={() => documentsTableRef.current?.refreshData()}
                isGroupPractice={isGroupPractice}
            />
            <SendFaxDrawer
                open={sendFaxOpen}
                onClose={closeSendFax}
                contacts={faxContacts}
                initialAttachment={sendFaxPrefill}
                onSendSuccess={() => documentsTableRef.current?.refreshData()}
                onContactsChanged={() => void fetchFaxContacts()}
            />
        </div>
    );
};

export default Documents;
